import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkle, TrendUp, Tag, Clock, Basket, Wallet, ChartLineUp,
  CaretRight, ArrowsClockwise, PiggyBank, Lightning, CheckCircle, SealCheck,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, shortDate } from "@/lib/format";
import { Card, Loading, EmptyState, SectionTitle, catColor } from "@/components/riq";

const ICONS = { chart: ChartLineUp, trend: TrendUp, tag: Tag, clock: Clock, basket: Basket, wallet: Wallet, shield: SealCheck };
const TONE = {
  good: { bg: "bg-[#E4F7ED]", fg: "text-[#22B573]" },
  warn: { bg: "bg-[#FFF1D6]", fg: "text-[#B25E00]" },
  neutral: { bg: "bg-riq-sage", fg: "text-riq-primary" },
};

export default function Insights() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const cur = user?.currency || "ZAR";
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/insights").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  if (data === null) return <Loading label="Reading your spending patterns…" />;
  if (data === false) return <div className="text-muted-foreground">Could not load insights.</div>;

  const { feed = [], savings = [], recurring = [] } = data;
  const dueSoon = recurring.filter((r) => r.status !== "upcoming");

  if (feed.length === 0 && savings.length === 0 && recurring.length === 0) {
    return (
      <EmptyState
        icon={Sparkle}
        title="Insights are on the way"
        subtitle="Scan a few more receipts and we'll surface smart tips, savings and your recurring buys automatically."
      />
    );
  }

  const goItem = (name) => navigate(`/items/${encodeURIComponent(name)}`);

  return (
    <div className="space-y-8">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Personal intelligence</div>
        <h1 className="font-head text-3xl sm:text-4xl font-bold tracking-tight text-riq-primary mt-1">Insights</h1>
      </div>

      {/* Subscriptions entry */}
      <button data-testid="insights-subscriptions-link" onClick={() => navigate("/subscriptions")} className="w-full text-left">
        <Card className="flex items-center gap-3 p-4 hover:-translate-y-0.5 transition-transform">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-riq-primary/10 text-riq-primary shrink-0"><ArrowsClockwise size={20} weight="duotone" /></div>
          <div className="flex-1 min-w-0">
            <div className="font-head font-semibold text-riq-primary text-sm">Subscriptions &amp; memberships</div>
            <div className="text-xs text-muted-foreground">Track recurring expenses and see your monthly commitment.</div>
          </div>
          <CaretRight size={16} className="text-neutral-300 shrink-0" />
        </Card>
      </button>

      {/* Smart insights feed */}
      {feed.length > 0 && (
        <div>
          <SectionTitle>Smart tips</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {feed.map((ins, i) => {
              const Icon = ICONS[ins.icon] || Sparkle;
              const tone = TONE[ins.tone] || TONE.neutral;
              const clickable = !!ins.item;
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                  <Card
                    data-testid={`insight-card-${i}`}
                    onClick={clickable ? () => goItem(ins.item) : undefined}
                    className={`h-full flex items-start gap-3.5 p-4 ${clickable ? "cursor-pointer hover:-translate-y-0.5 transition-transform" : ""}`}
                  >
                    <div className={`w-10 h-10 rounded-xl ${tone.bg} flex items-center justify-center shrink-0`}>
                      <Icon size={20} weight="duotone" className={tone.fg} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-head font-semibold text-riq-primary text-sm">{ins.title}</div>
                      <p className="text-sm leading-relaxed text-neutral-600 mt-0.5">{ins.text}</p>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recurring / buy soon */}
      {recurring.length > 0 && (
        <div>
          <SectionTitle
            action={dueSoon.length > 0 ? <span className="text-sm text-riq-primary2 font-semibold">{dueSoon.length} due soon</span> : null}
          >
            <span className="flex items-center gap-2"><ArrowsClockwise size={22} weight="duotone" /> Recurring purchases</span>
          </SectionTitle>
          <Card className="divide-y divide-riq-border p-0">
            {recurring.map((r) => (
              <button
                key={r.item}
                data-testid={`recurring-row-${r.item}`}
                onClick={() => goItem(r.item)}
                className="w-full flex items-center gap-4 px-5 py-4 hover:bg-secondary/50 transition-colors text-left"
              >
                <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${catColor(r.category)}22` }}>
                  <Basket size={18} weight="duotone" style={{ color: catColor(r.category) }} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{r.item}</div>
                  <div className="text-xs text-muted-foreground">Every ~{r.median_interval} days · {r.purchases} buys · last {shortDate(r.last_purchase)}</div>
                </div>
                <StatusBadge status={r.status} days={r.days_until} next={r.predicted_next} />
                <CaretRight size={16} className="text-neutral-300 shrink-0" />
              </button>
            ))}
          </Card>
        </div>
      )}

      {/* Savings opportunities */}
      {savings.length > 0 && (
        <div>
          <SectionTitle>
            <span className="flex items-center gap-2"><PiggyBank size={22} weight="duotone" /> Where you can save</span>
          </SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {savings.map((s) => (
              <Card key={s.item} data-testid={`savings-card-${s.item}`} onClick={() => goItem(s.item)} className="p-4 cursor-pointer hover:-translate-y-0.5 transition-transform">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-head font-semibold text-riq-primary text-sm truncate">{s.item}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Cheapest at <b>{s.cheapest_merchant}</b></div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Save / unit</div>
                    <div className="font-head font-bold text-[#22B573]">{money(s.saving_per_unit, cur, { decimals: 2 })}</div>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs">
                  <span className="rounded-full bg-[#E4F7ED] px-2.5 py-1 text-[#22B573] font-medium">{s.cheapest_merchant} {money(s.cheapest_price, cur, { decimals: 2 })}</span>
                  <span className="text-neutral-400">vs</span>
                  <span className="rounded-full bg-secondary px-2.5 py-1 text-neutral-500">{s.dearest_merchant} {money(s.dearest_price, cur, { decimals: 2 })}</span>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status, days, next }) {
  if (status === "overdue") {
    return <span className="rounded-full bg-[#FFECEC] text-[#FF6B6B] px-3 py-1 text-xs font-semibold shrink-0 flex items-center gap-1"><Lightning size={13} weight="fill" /> Overdue</span>;
  }
  if (status === "due_soon") {
    return <span className="rounded-full bg-riq-gold/25 text-[#B25E00] px-3 py-1 text-xs font-semibold shrink-0">Due in {Math.max(days, 0)}d</span>;
  }
  return <span className="rounded-full bg-secondary text-neutral-500 px-3 py-1 text-xs font-medium shrink-0 flex items-center gap-1"><CheckCircle size={13} weight="fill" className="text-[#42C97C]" /> {shortDate(next)}</span>;
}
